import { BackgroundImage, Box, SimpleGrid, Text, Anchor, Stack, Title, List, Center } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { MEDPLUM_PROJECT_ID, MEDPLUM_CLIENT_ID, MEDPLUM_RECAPTCHA_SITE_KEY } from '../config';
import backgroundImage from '../img/homePage/annie-spratt-6a3nqQ1YwBw-unsplash.jpg';
import { useEffect, useState, type JSX } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { CustomRegisterForm } from '../components/auth/CustomRegisterForm';
import { trackCompleteRegistration, trackViewContent } from '../components/FacebookPixel';

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const profile = useMedplumProfile();
  const medplum = useMedplum();

  // Parse URL params to detect if launched from mobile app
  const urlParams = new URLSearchParams(location.search);
  const redirectUri = urlParams.get('redirect_uri');
  const isFromMobileApp = !!redirectUri;

  // Check window size on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check initially
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    // Track page view
    trackViewContent({
      content_name: 'Registration Page',
      content_type: 'registration'
    });

    // Log if coming from mobile app
    if (isFromMobileApp) {
      console.log('📱 Registration page opened from mobile app with redirect URI:', redirectUri);

      // Validate redirect URI format to catch potential issues
      try {
        const url = new URL(redirectUri);
        console.log('📱 Redirect URI scheme:', url.protocol);

        if (!url.protocol.includes('feelheard')) {
          console.warn('📱 ⚠️ Redirect URI does not use expected custom scheme (feelheard://)');
        }
      } catch (e) {
        console.error('📱 ⚠️ Invalid redirect URI format:', e);
      }

      // Log current auth state
      try {
        const currentLogin = medplum.getActiveLogin();
        console.log('📱 Current login status on page load:', currentLogin ? 'already logged in' : 'not logged in');
      } catch (error) {
        console.error('📱 Error checking current login state:', error);
      }
    }

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, [isFromMobileApp, redirectUri]);

  const handleRegistrationSuccess = async (email?: string) => {
    // Track registration completion
    trackCompleteRegistration({
      content_name: 'New User Registration',
      status: 'complete'
    });

    // Redirect based on source
    if (isFromMobileApp && redirectUri) {
      console.log('📱 Registration successful, preparing to redirect to mobile app:', redirectUri);

      try {
        // Get current user's access token and profile information
        console.log('📱 Attempting to get current user profile');
        const profile = await medplum.getProfile();

        if (profile?.id) {
          console.log('📱 Got profile ID for auto-login:', profile.id);

          // Get current authentication state
          console.log('📱 Retrieving active login state for token extraction');
          const authState = await medplum.getActiveLogin();
          console.log('📱 Auth state retrieved:', authState ? 'present' : 'missing');
          
          // Get project ID from login state or use default
          const projectId = authState?.project?.id || MEDPLUM_PROJECT_ID;
          console.log('📱 Project ID for auto-login:', projectId);

          if (authState && authState.accessToken) {
            // Log token info (safely)
            console.log('📱 Obtained access token for auto-login:',
              authState.accessToken ? `${authState.accessToken.substring(0, 10)}...` : 'missing');
            console.log('📱 Refresh token status:',
              authState.refreshToken ? 'present' : 'missing');

            // Create a secure redirect with authentication information
            // We'll encode these separately to avoid issues with URL encoding
            const params = new URLSearchParams();
            params.set('success', 'true');
            params.set('access_token', authState.accessToken);

            if (authState.refreshToken) {
              params.set('refresh_token', authState.refreshToken);
              console.log('📱 Added refresh token to redirect params');
            } else {
              console.warn('📱 ⚠️ No refresh token available for redirect!');
            }

            if (profile.id) {
              params.set('profile_id', profile.id);
              console.log('📱 Added profile ID to redirect params:', profile.id);
            }
            
            if (email) {
              params.set('email', email);
              console.log('📱 Added email to redirect params');
            }
            
            if (projectId) {
              params.set('project_id', projectId);
              console.log('📱 Added project ID to redirect params:', projectId);
            }

            // Log the full redirect URL (with sensitive parts masked)
            const debugUrl = redirectUri + '?' +
              params.toString().replace(/access_token=([^&]{10})([^&]+)/, 'access_token=$1...')
                .replace(/refresh_token=([^&]{5})([^&]+)/, 'refresh_token=$1...');
            console.log('📱 Redirecting to mobile app with auth information:', debugUrl);

            // Add slight delay to ensure logs are captured before redirect
            setTimeout(() => {
              window.location.href = redirectUri + '?' + params.toString();
            }, 100);
          } else {
            console.warn('📱 ⚠️ No access token available in auth state, redirecting without auto-login');
            console.log('📱 Auth state details:', JSON.stringify({
              hasAccessToken: !!authState?.accessToken,
              hasRefreshToken: !!authState?.refreshToken,
              hasProfile: !!authState?.profile
            }));
            const params = new URLSearchParams({ success: 'true' });
            if (email) params.set('email', email);
            if (projectId) params.set('project_id', projectId);
            window.location.href = redirectUri + '?' + params.toString();
          }
        } else {
          console.warn('📱 ⚠️ No profile available, redirecting without auto-login');
          const params = new URLSearchParams({ success: 'true' });
          if (email) params.set('email', email);
          if (MEDPLUM_PROJECT_ID) params.set('project_id', MEDPLUM_PROJECT_ID);
          window.location.href = redirectUri + '?' + params.toString();
        }
      } catch (error) {
        console.error('📱 ⚠️ Error preparing auto-login:', error);
        console.error('📱 Error details:', JSON.stringify(error, null, 2));
        // Fall back to basic redirect if anything fails
        const params = new URLSearchParams({ success: 'true' });
        if (email) params.set('email', email);
        if (MEDPLUM_PROJECT_ID) params.set('project_id', MEDPLUM_PROJECT_ID);
        window.location.href = redirectUri + '?' + params.toString();
      }
    } else {
      // Standard web flow
      console.log('Registration successful, navigating to home page');
      navigate('/');
    }
  };
  
  // Mobile app styling (matching sign-in.tsx with image header)
  if (isFromMobileApp && isMobile) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #3C79C9 0%, #1D1D43 100%)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header image matching Cora app */}
        <Box
          style={{
            width: '100%',
            height: '300px',
            backgroundImage: 'url(/assets/two-hearts-gradient.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        {/* Form content */}
        <Box 
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <Stack align="center" gap="lg" style={{ width: '100%', maxWidth: '350px' }}>
            
            <Box style={{ 
              width: '100%',
              fontFamily: 'Poppins, sans-serif'
            }}>
            <style>{`
              /* Hide red asterisks for required fields in mobile app version */
              .mantine-InputWrapper-required {
                display: none !important;
              }
              /* Apply Poppins font to all form elements */
              .mantine-TextInput-input,
              .mantine-PasswordInput-input,
              .mantine-TextInput-label,
              .mantine-PasswordInput-label,
              .mantine-Button-root,
              .mantine-Text-root,
              .mantine-Anchor-root {
                font-family: 'Poppins, sans-serif' !important;
              }
            `}</style>
            <CustomRegisterForm
              type="patient"
              projectId={MEDPLUM_PROJECT_ID}
              clientId={MEDPLUM_CLIENT_ID}
              recaptchaSiteKey={MEDPLUM_RECAPTCHA_SITE_KEY}
              onSuccess={handleRegistrationSuccess}
              isMobileApp={true}
              redirectUri={redirectUri}
            />
          </Box>
          
          <Box mt="md" style={{ textAlign: 'center' }}>
            <Text 
              size="sm" 
              c="rgba(255, 255, 255, 0.8)"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Already have an account?{' '}
              <Anchor 
                href={redirectUri + '?cancel=true'} 
                c="white"
                style={{ 
                  textDecoration: 'underline',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                Return to app
              </Anchor>
            </Text>
          </Box>
          </Stack>
        </Box>
      </Box>
    );
  }

  // Standard web layout
  return (
    <SimpleGrid cols={isMobile ? 1 : 2} spacing={0}>
      <Box 
        pt={isMobile ? 50 : 100} 
        pb={isMobile ? 50 : 200}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%'
        }}
      >
        <Box 
          style={{
            maxWidth: 450,
            width: '100%',
            margin: '0 auto',
            padding: isMobile ? '0 16px' : 0
          }}
        >
          <Stack align="center" mb="md">
            <Title order={2} c="teal.6" fw={700} style={{ fontFamily: 'Poppins, sans-serif' }}>Create Your Account</Title>
            <Text c="dimmed" style={{ fontFamily: 'Poppins, sans-serif' }}>Begin your personal wellness journey today</Text>
          </Stack>
          
          <CustomRegisterForm
            type="patient"
            projectId={MEDPLUM_PROJECT_ID}
            clientId={MEDPLUM_CLIENT_ID}
            recaptchaSiteKey={MEDPLUM_RECAPTCHA_SITE_KEY}
            onSuccess={handleRegistrationSuccess}
          />
          
          <Box mt={20} style={{ textAlign: 'center' }}>
            <Text size="sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Already have an account?{' '}
              <Anchor onClick={() => navigate('/signin')} c="teal.6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Sign in
              </Anchor>
            </Text>
          </Box>
        </Box>
      </Box>
      
      {!isMobile && (
        <BackgroundImage 
          src={backgroundImage} 
          style={{
            height: '100vh',
            position: 'relative'
          }}
        >
          <Box 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(20, 184, 166, 0.3)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4rem'
            }}
          >
            <Box maw={450} c="white">
              <Title order={1} mb="lg" style={{ fontFamily: 'Poppins, sans-serif' }}>Start Your Wellness Journey</Title>
              <Text size="lg" mb="xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Join others who have found support, understanding, and personal growth with our platform.
              </Text>
              <List spacing="sm" size="lg">
                <List.Item icon="✓">Experience compassionate emotional support</List.Item>
                <List.Item icon="✓">Develop deeper self-awareness and insights</List.Item>
                <List.Item icon="✓">Access guidance whenever you need it most</List.Item>
              </List>
            </Box>
          </Box>
        </BackgroundImage>
      )}
    </SimpleGrid>
  );
}